import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScriptPicker from "@/components/ScriptPicker";
import LanguagePicker from "@/components/LanguagePicker";
import CommunityCard from "@/components/CommunityCard";
import { useI18n } from "@/contexts/I18nContext";
import { resolveModuleName } from "@/lib/i18nContent";
import { useOffline } from "@/contexts/OfflineContext";
import { listModules, getModule } from "@/lib/apiClient";
import { downloadModule, estimateModuleBytes, formatBytes } from "@/lib/offlineCache";
import { Plane, Loader2, ArrowRight } from "lucide-react";

/**
 * Settings — user preferences. Script (Mushaf), Language, Travel mode
 * (offline downloads), and the Community teaser card.
 */
export default function Settings() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen flex flex-col" data-testid="settings-page">
      <Header />
      <main className="flex-1 max-w-[768px] mx-auto w-full px-5 sm:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.22em] text-[#4B5563]">{t("settings.eyebrow")}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-[#1F2937]">{t("settings.title")}</h1>
        <p className="text-[#4B5563] mt-3">{t("settings.subtitle")}</p>

        <div className="mt-8" data-testid="settings-community-section">
          <CommunityCard />
        </div>

        <section className="mt-6 rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-8" data-testid="settings-language-section">
          <LanguagePicker />
        </section>

        <section className="mt-6 rounded-3xl bg-white border border-[#E7E0D2] p-6 sm:p-8" data-testid="settings-script-section">
          <ScriptPicker />
        </section>

        <section className="mt-6" data-testid="settings-travel-section">
          <TravelModeSection />
        </section>
      </main>
      <Footer />
    </div>
  );
}

/**
 * Travel mode — offline downloads. Moved here from the Dashboard (was a
 * dismissible one-shot nudge there); Settings is a stable destination so
 * it's now a persistent action instead.
 */
function TravelModeSection() {
  const { t } = useI18n();
  const { online, downloads, refresh } = useOffline();
  const [modules, setModules] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total, code, name }

  useEffect(() => { listModules().then((r) => setModules(r?.modules || r || [])).catch(() => {}); }, []);

  const saveTop3 = async () => {
    if (busy || !modules.length) return;
    setBusy(true);
    const top3 = modules.slice(0, 3);
    try {
      let i = 0;
      for (const m of top3) {
        i += 1;
        setProgress({ current: i, total: top3.length, code: m.code, name: resolveModuleName(t, m) });
        const detail = await getModule(m.code);
        await downloadModule(detail.module);
      }
      await refresh();
      toast.success(t("travel.toast_saved", { count: top3.length }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t("travel.toast_download_failed"));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const estTotal = modules.slice(0, 3).reduce((s, m) => s + estimateModuleBytes(m), 0);

  return (
    <div data-testid="travel-mode-card" className="rounded-3xl bg-gradient-to-br from-[#FFF7ED] to-[#FED7AA]/40 border-2 border-[#FED7AA] p-6 sm:p-7 relative overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="w-14 h-14 rounded-2xl bg-[#D97706] grid place-items-center text-white shrink-0 rotate-[-8deg]">
          <Plane className="w-7 h-7" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <div className="text-xs uppercase tracking-[0.22em] text-[#B45309] font-semibold">{t("travel.eyebrow")}</div>
          <h3 className="font-heading text-xl sm:text-2xl text-[#1F2937] mt-1.5">{t("travel.title")}</h3>
          <p className="text-sm text-[#4B5563] mt-1.5 leading-relaxed">
            {t("travel.body")}{" "}
            {modules.length > 0 && <span className="text-[#4B5563]/70">{t("travel.total_size", { size: formatBytes(estTotal) })}</span>}
          </p>
          {!online && <p className="mt-2 text-xs text-[#B45309]">{t("travel.offline_hint")}</p>}
          {downloads.length > 0 && <p className="mt-2 text-xs text-[#064E3B]">{t("travel.already_saved", { count: downloads.length })}</p>}
          {progress && (
            <div className="mt-3 text-xs text-[#B45309] font-medium" data-testid="travel-mode-progress">
              {t("travel.downloading_progress", { current: progress.current, total: progress.total, name: progress.name })}
            </div>
          )}
        </div>
        <div className="flex sm:flex-col gap-2 sm:gap-2">
          <button
            data-testid="travel-mode-save"
            onClick={saveTop3}
            disabled={busy || !online || !modules.length}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:bg-[#9CA3AF] disabled:cursor-not-allowed text-white text-sm font-medium transition whitespace-nowrap shadow-sm">
            {busy
              ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("travel.saving")}</>
              : <>{t("travel.save_top_3")} <ArrowRight className="w-4 h-4" /></>}
          </button>
          <Link
            data-testid="travel-mode-customize"
            to="/modules"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white border border-[#FED7AA] hover:border-[#D97706] text-[#B45309] text-sm font-medium transition whitespace-nowrap">
            {t("travel.choose_modules")}
          </Link>
        </div>
      </div>
    </div>
  );
}
